/**
 * Bero platform console — Leads & prospects. GET /api/platform/leads lists
 * every Society with status LEAD/PROSPECT (the public "ask for Bero" funnel
 * lands rows here — nothing to seed, they show up on their own). SALES and
 * SUPER_ADMIN can edit lead_status (tap a Chip, optimistic PATCH) and notes
 * (expand, blur = PATCH); everyone else on the platform team sees it
 * read-only. Convert hands off to /platform/create?leadId=&name=&city= —
 * the actual society-creation POST still requires SUPER_ADMIN server-side.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import type { PillStatus } from '@bero/tokens';
import { space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { useFetch } from '@/features/community/lib';
import { usePlatformRole } from '@/features/platform/PlatformGate';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Chip, EmptyState, Hairline, Input, Pill, Screen, Skeleton } from '@/ui';

type Lead = {
  id: number; name: string; city: string; status: 'LEAD' | 'PROSPECT';
  leadStatus: 'NEW' | 'CONTACTED' | 'CONVERTED' | 'CLOSED';
  leadNotes: string; leadAssigneeUserId: number | null; createdAt: string | null;
};

async function fetchLeads(): Promise<Lead[]> {
  const body = await api.get<{ items: Lead[] }>('/api/platform/leads');
  return Array.isArray(body.items) ? body.items : [];
}

const STAGE_TONE: Record<Lead['status'], PillStatus> = { LEAD: 'pending', PROSPECT: 'open' };
const STAGE_LABEL: Record<Lead['status'], DictKey> = { LEAD: 'plat_lead_stage_lead', PROSPECT: 'plat_lead_stage_prospect' };
const LEAD_STATUSES: Lead['leadStatus'][] = ['NEW', 'CONTACTED', 'CONVERTED', 'CLOSED'];
const STATUS_LABEL: Record<Lead['leadStatus'], DictKey> = {
  NEW: 'plat_lead_status_new', CONTACTED: 'plat_lead_status_contacted',
  CONVERTED: 'plat_lead_status_converted', CLOSED: 'plat_lead_status_closed',
};

function LeadRow({
  lead, canEdit, onPatch,
}: {
  lead: Lead; canEdit: boolean;
  onPatch: (id: number, body: Partial<Pick<Lead, 'leadStatus' | 'leadNotes'>>) => Promise<void>;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  const [expanded, setExpanded] = React.useState(false);
  const [notes, setNotes] = React.useState(lead.leadNotes);
  const [err, setErr] = React.useState(false);

  React.useEffect(() => { void Promise.resolve().then(() => setNotes(lead.leadNotes)); }, [lead.leadNotes]);

  const changeStatus = async (leadStatus: Lead['leadStatus']) => {
    if (!canEdit || leadStatus === lead.leadStatus) return;
    setErr(false);
    try {
      await onPatch(lead.id, { leadStatus });
    } catch {
      setErr(true);
    }
  };

  const saveNotes = async () => {
    if (!canEdit || notes === lead.leadNotes) return;
    setErr(false);
    try {
      await onPatch(lead.id, { leadNotes: notes });
    } catch {
      setErr(true);
    }
  };

  return (
    <View style={{ paddingVertical: space.s3, paddingHorizontal: space.s2, gap: space.s2 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <AppText variant="bodyMd" weight="500" numberOfLines={1}>{lead.name}</AppText>
          <AppText variant="bodySm" tone="tertiary" numberOfLines={1}>{lead.city}</AppText>
        </View>
        <Pill status={STAGE_TONE[lead.status]} label={t(STAGE_LABEL[lead.status])} />
        {canEdit ? (
          <Pressable
            accessibilityRole="button"
            onPress={() => router.push(`/platform/create?leadId=${lead.id}&name=${encodeURIComponent(lead.name)}&city=${encodeURIComponent(lead.city)}`)}
            hitSlop={6}
          >
            <AppText variant="bodySm" weight="600" tone="brand">{t('plat_lead_convert')}</AppText>
          </Pressable>
        ) : null}
      </View>

      <View style={{ flexDirection: 'row', gap: space.s2, flexWrap: 'wrap' }}>
        {LEAD_STATUSES.map((s) => (
          <Chip
            key={s}
            label={t(STATUS_LABEL[s])}
            active={lead.leadStatus === s}
            onPress={canEdit ? () => void changeStatus(s) : undefined}
          />
        ))}
      </View>

      <Pressable accessibilityRole="button" onPress={() => setExpanded((e) => !e)} hitSlop={4}>
        <AppText variant="bodySm" tone="tertiary" numberOfLines={expanded ? undefined : 1}>
          {expanded ? t('plat_lead_notes_ph') : (lead.leadNotes || t('plat_lead_notes_ph'))}
        </AppText>
      </Pressable>
      {expanded ? (
        <Input
          value={notes}
          onChangeText={setNotes}
          onBlur={() => void saveNotes()}
          placeholder={t('plat_lead_notes_ph')}
          multiline
          editable={canEdit}
          style={{ minHeight: 60, textAlignVertical: 'top' }}
        />
      ) : null}
      {err ? <AppText variant="bodySm" style={{ color: colors.danger500 }}>{t('plat_lead_save_err')}</AppText> : null}
    </View>
  );
}

export default function PlatformLeadsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const role = usePlatformRole();
  const canEdit = role === 'SALES' || role === 'SUPER_ADMIN';
  const { data, loading, error, refetch, mutate } = useFetch(fetchLeads, []);
  const rows = data ?? [];

  const patch = async (id: number, body: Partial<Pick<Lead, 'leadStatus' | 'leadNotes'>>) => {
    const prev = data;
    mutate((p) => (p ? p.map((l) => (l.id === id ? { ...l, ...body } : l)) : p));
    try {
      await api.patch(`/api/platform/leads/${id}`, body);
    } catch (e) {
      mutate(() => prev ?? null);
      throw e;
    }
  };

  return (
    <Screen padded fullWidth style={{ paddingTop: space.s5 }}>
      {loading ? (
        <View style={{ gap: space.s2 }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} h={80} />)}
        </View>
      ) : error ? (
        <EmptyState
          icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
          title={t('plat_leads_err')}
          cta={t('retry')}
          onCta={() => void refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Icon name="building" size={40} color={colors.textTertiary} />}
          title={t('plat_leads_empty')}
        />
      ) : (
        <View>
          {rows.map((lead, i) => (
            <View key={lead.id}>
              <LeadRow lead={lead} canEdit={canEdit} onPatch={patch} />
              {i < rows.length - 1 ? <Hairline /> : null}
            </View>
          ))}
        </View>
      )}
    </Screen>
  );
}
